import { USER_FEATURE_NAMES } from '@constants/featureFlags';
import { getEffectiveIsEnabledForUserByFeatureName } from '@store/Global/service';
import { LtlQuoteParametersViewModel } from '@store/orders/types';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { produce } from 'solid-js/store';

import { customerDetailsStore, setCustomerDetailsStore } from '../store';
import { CustomerCreditReviewFlag } from '../types';
import {
  createAlternateCustomer,
  createCustomerCreditFlag,
  createScacAccount,
  deleteAlternateCustomer,
  deleteCustomerCreditFlag,
  deleteScacAccount,
  fetchActiveScacAccounts,
  fetchAlternateCustomers,
  fetchBillToName,
  fetchCustomerLTLData,
  fetchEvaAccessIdRequirements,
  fetchLtlCustomerSetting,
  fetchPotentialAlternateCustomerOptions,
  fetchPotentialScacAccounts,
  fetchScacAccountTypes,
  getCreditFlagTypes,
  getCustomerCreditFlags,
  getTestLane,
  updateCarrierSelection,
  updateCustomerCreditFlag,
  updateCustomerLTLData,
  updateLtlCustomerSetting,
  updateScacAccount,
} from './services';
import {
  setCustomerLTLSettingsLegacy,
  setLtlCustomerSetting,
  setTestLaneStore,
} from './store';
import {
  CarrierScac,
  CustomerLookup,
  EvaAccessIdRequirement,
  ILTLCustomerData,
  ILtlCustomerSettingPayload,
  ScacAccount,
  ScacAccountCustomer,
  ScacAccountTypes,
} from './types';

export const getCustomerLTLSettingsLegacy = async (customerId: string) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchCustomerLTLData(customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.settings = resp as ILTLCustomerData;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.settings = {} as ILTLCustomerData;
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(`Failed to fetch customer Data: ${error.message}`);
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const saveCustomerLTLSettingsLegacy = async (
  payload: ILTLCustomerData,
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await updateCustomerLTLData(payload, changeTrackingSources);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.settings = resp as ILTLCustomerData;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    return resp;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getCustomerSpecificPricingData = async (customerId: number) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchActiveScacAccounts(customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.ltlScacAccounts = resp as unknown as ScacAccount[];
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.ltlScacAccounts = {} as ScacAccount[];
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(`Failed to fetch Scac Account Data: ${error.message}`);
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getCspCarrierOptions = async (customerId: number) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchPotentialScacAccounts(customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.ltlCspCarrierOptions = resp as unknown as CarrierScac[];
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.ltlCspCarrierOptions = {} as CarrierScac[];
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(
        `Failed to fetch valid carriers to add CSP to: ${error.message}`,
      );
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getScacAccountTypes = async () => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchScacAccountTypes();
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.ltlScacAccountTypes =
          resp as unknown as ScacAccountTypes;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.ltlScacAccountTypes = {} as ScacAccountTypes;
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(`Failed to fetch Scac Account Types: ${error.message}`);
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getEvaAccessIdRequirements = async (
  customerId: number,
  carrierScacId: number,
) => {
  try {
    const evaAccessIdRequirements = await fetchEvaAccessIdRequirements(
      customerId,
      carrierScacId,
    );
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.evaAccessIdRequirements =
          evaAccessIdRequirements as unknown as EvaAccessIdRequirement;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    return evaAccessIdRequirements;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.evaAccessIdRequirements = {} as EvaAccessIdRequirement;
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(
        `Failed to fetch EVA Access ID requirements: ${error.message}`,
      );
    }
  }
};

export const createNewScacAccount = async (
  payload: Partial<ScacAccount>,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    if (changeTrackingSource) {
      await createScacAccount(payload, changeTrackingSource);
    } else {
      await createScacAccount(payload);
    }

    return true;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const updateExistingScacAccount = async (
  payload: ScacAccount,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    if (changeTrackingSource) {
      await updateScacAccount(payload, changeTrackingSource);
    } else {
      await updateScacAccount(payload);
    }

    return true;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const deleteExistingScacAccount = async (
  scacAccountId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    if (changeTrackingSource) {
      await deleteScacAccount(scacAccountId, changeTrackingSource);
    } else {
      await deleteScacAccount(scacAccountId);
    }

    return true;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getPotentialAlternateCustomerOptions = async (
  customerId: number,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchPotentialAlternateCustomerOptions(customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.alternateCustomerOptions =
          resp as unknown as CustomerLookup[];
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.alternateCustomerOptions = {} as CustomerLookup[];
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(
        `Failed to fetch alternate customer options: ${error.message}`,
      );
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const getAlternateCustomers = async (customerId: number) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    const resp = await fetchAlternateCustomers(customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.scacAccountCustomer = resp as ScacAccountCustomer;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
        draft.cspSetup.scacAccountCustomer = {} as ScacAccountCustomer;
      }),
    );
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(`Failed to fetch alternate customers: ${error.message}`);
    }
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const addAlternateCustomer = async (
  customerId: number,
  alternateCustomerId: number,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    await createAlternateCustomer(customerId, alternateCustomerId);

    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.cspSetup.alternateCustomerOptions =
          draft.cspSetup.alternateCustomerOptions.filter(
            (opt) => opt.customerId !== alternateCustomerId,
          );
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );

    return true;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const removeAlternateCustomer = async (
  alternateCustomer: CustomerLookup,
) => {
  setCustomerLTLSettingsLegacy('isLoading', true);
  try {
    await deleteAlternateCustomer(alternateCustomer.customerId);
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        const exists = draft.cspSetup.alternateCustomerOptions.some(
          (opt) => opt.customerId === alternateCustomer.customerId,
        );

        if (!exists) {
          draft.cspSetup.alternateCustomerOptions.push(alternateCustomer);
        }

        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    return true;
  } catch (error) {
    setCustomerLTLSettingsLegacy(
      produce((draft) => {
        draft.isError = true;
      }),
    );
    throw error;
  } finally {
    setCustomerLTLSettingsLegacy('isLoading', false);
  }
};

export const createCustomerCreditFlagAction = async (
  payload: CustomerCreditReviewFlag,
) => {
  const res = await createCustomerCreditFlag(payload);
  if (res) {
    const arr = [...customerDetailsStore.customerFlagReviewData, ...res.value];
    setCustomerDetailsStore('customerFlagReviewData', arr);
  }
  return Boolean(res);
};

export const updateCustomerCreditFlagAction = async (
  payload: CustomerCreditReviewFlag,
) => {
  const res = await updateCustomerCreditFlag(payload);
  if (res) {
    const arr = customerDetailsStore.customerFlagReviewData.map((item) =>
      item.id === payload.id ? res.value : item,
    );
    setCustomerDetailsStore('customerFlagReviewData', arr);
  }
  return Boolean(res);
};

export const getBillToName = async (
  customerId: number,
  carrierScacId: number,
) => {
  try {
    const billToName = await fetchBillToName(customerId, carrierScacId);
    return billToName as string;
  } catch (error) {
    if (error instanceof Error) {
      setCustomerLTLSettingsLegacy('errorMessage', error.message);
      throw new Error(
        `Failed to fetch Bill To Name for Customer: ${error.message}`,
      );
    }
  }
};

export const getCustomerCreditFlagsAction = async (customerId: number) => {
  const resp = await getCustomerCreditFlags(customerId, false);
  if (resp?.isSuccess)
    setCustomerDetailsStore('customerFlagReviewData', resp.value);
};

/**
 * Both flag types are gated by per-user feature flags (VNEXT-14228). Fetched
 * once on the customer screen so the header button, the banners and the modal
 * all read the same answer.
 */
export const getCustomerFlagPermissionsAction = async () => {
  const [canFlagForReview, canFlagFinancialExposure] = await Promise.all([
    getEffectiveIsEnabledForUserByFeatureName(
      USER_FEATURE_NAMES.customerFlagForReview,
    ),
    getEffectiveIsEnabledForUserByFeatureName(
      USER_FEATURE_NAMES.customerFlagForFinancialExposure,
    ),
  ]);

  setCustomerDetailsStore('customerFlagPermissions', {
    canFlagForReview: canFlagForReview ?? false,
    canFlagFinancialExposure: canFlagFinancialExposure ?? false,
  });
};

/**
 * Flag types are a BE lookup (`Flag For Review` = 6, `Flag for Financial
 * Exposure` = 7). Fetched once per customer screen mount and cached in the
 * store so the modal never has to hardcode the list.
 */
export const getCreditFlagTypesAction = async () => {
  if (customerDetailsStore.customerFlagTypes.length > 0) return;
  const resp = await getCreditFlagTypes();
  if (resp?.isSuccess) setCustomerDetailsStore('customerFlagTypes', resp.value);
};

export const deleteCustomerCreditFlagAction = async (id: number) => {
  try {
    setCustomerDetailsStore(
      'customerFlagReviewModalModeAndLoading',
      'loading',
      true,
    );
    const res = await deleteCustomerCreditFlag(id);
    if (res) {
      const arr = customerDetailsStore.customerFlagReviewData.filter(
        (item) => item.id !== id,
      );
      setCustomerDetailsStore('customerFlagReviewData', arr);
    }
    return Boolean(res);
  } finally {
    setCustomerDetailsStore(
      'customerFlagReviewModalModeAndLoading',
      'loading',
      false,
    );
  }
};

export const getLtlCustomerSetting = async (customerId: number) => {
  setLtlCustomerSetting('isLoading', true);
  try {
    const resp = await fetchLtlCustomerSetting(customerId);
    setLtlCustomerSetting(
      produce((draft) => {
        draft.data = resp;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } catch (error) {
    setLtlCustomerSetting(
      produce((draft) => {
        draft.data = {
          customerId,
          quoteExpirationDays: null,
          carrierSelectionStrategyType: null,
          enableCustomerPortalAutoDispatch: null,
          enableSuggestedClass: null,
          enableAgentQuotesInCustomerPortal: null,
          enableCustomerSpecificPricing: null,
          enableLtlInCustomerPortal: null,
          enableMixedCommodities: null,
          enablePrePro: null,
          emailAgentIfShipmentNotBuilt: null,
          emailAgentIfNoQuoteResult: null,
          disableBolInCustomerPortal: null,
          enableLtlGapLogic: null,
          gapCompressionPercentage: null,
          insuranceTermsAccepted: null,
          carriers: [],
        };
        draft.isError = true;
        draft.errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to fetch LTL customer setting.';
      }),
    );
  } finally {
    setLtlCustomerSetting('isLoading', false);
  }
};

export const saveLtlCustomerSetting = async (
  payload: ILtlCustomerSettingPayload,
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  setLtlCustomerSetting('isLoading', true);
  try {
    const resp = await updateLtlCustomerSetting(payload, changeTrackingSources);
    if (resp.isSuccess) {
      await getLtlCustomerSetting(payload.customerId);
      return true;
    }
  } catch {
    // error already toasted in service
  } finally {
    setLtlCustomerSetting('isLoading', false);
  }
};

export const saveCarrierSelection = async (
  customerId: number,
  carrierSelectionStrategyType: number | null,
  carrierIds: number[],
  changeTrackingSources?: ChangeTrackingSource[],
) => {
  if (carrierSelectionStrategyType == null) return;
  setLtlCustomerSetting('isLoading', true);
  try {
    const resp = await updateCarrierSelection(
      customerId,
      carrierSelectionStrategyType,
      carrierIds,
      changeTrackingSources,
    );
    if (resp.isSuccess) {
      await getLtlCustomerSetting(customerId);
      return true;
    }
  } catch {
    // error already toasted in service
  } finally {
    setLtlCustomerSetting('isLoading', false);
  }
};

export const getTestLaneResults = async (
  scacAccount: ScacAccount,
  quoteParameters: LtlQuoteParametersViewModel,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  setTestLaneStore('isLoading', true);
  try {
    const response = changeTrackingSource
      ? await getTestLane(scacAccount, quoteParameters, changeTrackingSource)
      : await getTestLane(scacAccount, quoteParameters);

    if (!response) {
      throw new Error('No response received from test lane service');
    }

    const sortedResults = [...response.value.quotes].sort((a, b) => {
      if (
        a.serviceTypeDisplayName === 'Standard Contract' &&
        b.serviceTypeDisplayName !== 'Standard Contract'
      )
        return -1;
      if (
        a.serviceTypeDisplayName !== 'Standard Contract' &&
        b.serviceTypeDisplayName === 'Standard Contract'
      )
        return 1;

      return a.carrierTotal - b.carrierTotal;
    });

    setTestLaneStore(
      produce((draft) => {
        draft.testResultsSuccess = sortedResults;
        draft.testResultsFailure = response.value.failedQuotes;
        draft.hasTestedLane = true;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );

    return {
      results: sortedResults,
      failedResults: response.value.failedQuotes,
    };
  } catch (error) {
    setTestLaneStore(
      produce((draft) => {
        draft.isError = true;
        draft.testResultsSuccess = [];
        draft.testResultsFailure = [];
        draft.hasTestedLane = false;
        draft.errorMessage =
          error instanceof Error ? error.message : 'Failed to test lane';
      }),
    );
    return [];
  } finally {
    setTestLaneStore('isLoading', false);
  }
};
